import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { create } from '../../actions/conversation';
import { trackConversionEvent } from '../../actions/tracking';
import ReCaptcha from '../../components/re-captcha/re-captcha';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import { isAuthenticated } from '../../selectors/auth';
import { getRetailerDocument } from '../../selectors/retailer';
import Alert from '../../styled-components/components/Alert';
import { Button } from '../../styled-components/components/Button';
import InputError from '../../styled-components/components/InputError';
import InputLabel from '../../styled-components/components/InputLabel';
import InputText from '../../styled-components/components/InputText';
import InputTextarea from '../../styled-components/components/InputTextarea';
import Loader from '../../styled-components/components/Loader';
import Modal from '../../styled-components/components/Modal';
import { StackLarge, StackMedium } from '../../styled-components/components/Stack';
import SignInModal from '../../styled-components/structures/Modals/SignInModal';

const validate = (state: State) => {
  if (!state.title || state.title.length < 3) {
    return false;
  }

  if (!state.message || state.message.length < 5) {
    return false;
  }

  if (!state.captcha) {
    return false;
  }

  return true;
};

interface State {
  title: string;
  message: string;
  captcha?: string;
  valid: boolean;
  success: boolean;
  failed: boolean;
  messageError: boolean;
  titleError: boolean;
}

interface MessageToRetailerModalProps {
  onClose: () => void;
}

const MessageToRetailerModal = ({ onClose }: MessageToRetailerModalProps) => {
  const { t } = useTranslation(['retailer']);
  const dispatch = useDispatch();

  const requestStatus = useSelector<RootState, number>((s) => s.conversations.requestStatus);
  const responseStatus = useSelector<RootState, number>((s) => s.conversations.responseStatus);
  const authenticated = useSelector(isAuthenticated);
  const retailer = useSelector(getRetailerDocument);

  const actions = bindActionCreators({ create, trackConversionEvent }, dispatch);

  const requestCreate = useDataExchangeStatus(ExchangeTypes.Creating, [
    requestStatus,
    responseStatus,
  ]);

  const [state, setState] = useState<State>({
    title: '',
    message: '',
    valid: false,
    success: false,
    failed: false,
    messageError: false,
    titleError: false,
  });

  const onValueChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextState = { ...state };
    nextState[e.currentTarget.name as 'title' | 'message'] = e.currentTarget.value;
    nextState.valid = validate(nextState);

    setState(nextState);
  };

  const onReCaptchaChange = (captcha: string | null) => {
    const nextState = { ...state };
    nextState.captcha = captcha || undefined;
    nextState.valid = validate(nextState);
    setState(nextState);
  };

  const onSend = () => {
    actions.create({
      branchId: retailer?.id,
      title: state.title,
      messageText: state.message,
      captcha: state.captcha,
    });
    actions.trackConversionEvent(retailer, 'message-sent-retailer');
  };

  useEffect(() => {
    if (state.message.length >= 5) {
      setState((s) => {
        const state = { ...s };
        state.messageError = false;
        return state;
      });
    }
    if (state.title.length >= 3) {
      setState((s) => {
        const state = { ...s };
        state.titleError = false;
        return state;
      });
    }
  }, [state.message, state.title]);

  const onMessageBlur = () => {
    if (state.message.length < 5) {
      setState((s) => {
        const state = { ...s };
        state.messageError = true;
        return state;
      });
    }
  };

  const onTitleBlur = () => {
    if (state.title.length < 3) {
      setState((s) => {
        const state = { ...s };
        state.titleError = true;
        return state;
      });
    }
  };

  if (requestCreate.pending) {
    return (
      <Modal onClose={onClose}>
        <Loader />
      </Modal>
    );
  }

  if (requestCreate.successful) {
    return (
      <Modal heading={t('messageSent', { ns: 'retailer' })} onClose={onClose}>
        <Alert tone="success">
          {t('messageSentAlert', {
            ns: 'retailer',
            retailer: retailer?.name || t('theRetailer', { ns: 'retailer' }).toLowerCase(),
          })}
        </Alert>
      </Modal>
    );
  }

  if (authenticated) {
    return (
      <Modal
        heading={t('contactRetailer', {
          ns: 'retailer',
          retailer: retailer?.name || t('theRetailer', { ns: 'retailer' }).toLowerCase(),
        })}
        onClose={onClose}
      >
        <StackLarge>
          {requestCreate.failed && (
            <StackMedium>
              <Alert tone="error">{t('couldNotSendMessage', { ns: 'retailer' })}</Alert>
            </StackMedium>
          )}
          <StackMedium>
            <InputLabel htmlFor="message-modal-title">
              {t('subject', { ns: 'retailer' })}
            </InputLabel>
            <InputText
              type="text"
              name="title"
              placeholder={t('subject', { ns: 'retailer' })}
              id="message-modal-title"
              value={state.title}
              onChange={onValueChange}
              onBlur={onTitleBlur}
              hasError={state.titleError}
            />
            {state.titleError && (
              <InputError>
                {t('errorValueNeedMoreThanNumberOfCharacters', { ns: 'validation', count: 3 })}
              </InputError>
            )}
          </StackMedium>
          <StackMedium>
            <InputLabel htmlFor="message-textarea">
              {t('yourMessage', { ns: 'retailer' })}
            </InputLabel>
            <InputTextarea
              name="message"
              placeholder={t('yourMessagePlaceholder', { ns: 'retailer' })}
              onChange={onValueChange}
              onBlur={onMessageBlur}
              value={state.message}
              id="message-textarea"
              hasError={state.messageError}
            />
            {state.messageError && (
              <InputError>
                {t('errorValueNeedMoreThanNumberOfCharacters', { ns: 'validation', count: 5 })}
              </InputError>
            )}
          </StackMedium>
          <StackMedium>
            <ReCaptcha onChange={onReCaptchaChange} />
          </StackMedium>
        </StackLarge>
        <StackLarge>
          <Button
            $tone="primary"
            $fullWidth
            disabled={!state.valid}
            onClick={onSend}
            title={t('sendTitle', { ns: 'retailer' })}
          >
            {t('send', { ns: 'retailer' })}
          </Button>
        </StackLarge>
      </Modal>
    );
  }

  return (
    <SignInModal
      title={t('sendMessageAuthModalTitle', { ns: 'retailer' })}
      text={t('loginModalDescription', { ns: 'loginModal' })}
      image="/images/modal/message_retailer.svg"
      onClose={onClose}
    />
  );
};

export default MessageToRetailerModal;
